import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, ClipboardList, CheckCircle2, Plus, Trash2, FileDown,
  ArrowLeft, Save, MessageSquareText, ListChecks, AlertTriangle, Sparkles, PenLine,
  ShieldCheck
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import CloseOutDialog from "@/components/CloseOutDialog";
import { formatDate, formatDateTime } from "@/lib/format";

export default function MeetingRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [instance, setInstance] = useState(null);
  const [newAttendee, setNewAttendee] = useState({ name: "", email: "" });
  const [newAgenda, setNewAgenda] = useState("");
  const [newAction, setNewAction] = useState({ description: "", owner_name: "", owner_email: "", due_date: "", priority: "normal" });
  const [discussion, setDiscussion] = useState("");
  const [minutes, setMinutes] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);

  const load = async () => {
    const r = await api.get(`/meetings/${id}`);
    setM(r.data);
    setDiscussion(r.data.discussion || "");
    setMinutes(r.data.minutes || "");
    if (r.data.report_instance_id) {
      try {
        const ir = await api.get(`/report-instances/${r.data.report_instance_id}`);
        setInstance(ir.data);
      } catch {}
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!m) return <div className="p-10 font-mono text-[#1a1a1a]/60">Loading meeting…</div>;

  const patch = async (body) => {
    await api.patch(`/meetings/${id}`, body);
    setSavedAt(new Date());
    load();
  };

  const setAttendees = (next) => patch({ attendees: next });
  const toggleAttendee = (i) => {
    const next = [...m.attendees];
    next[i] = { ...next[i], present: !next[i].present };
    setAttendees(next);
  };
  const addAttendee = () => {
    if (!newAttendee.name) return;
    setAttendees([...(m.attendees || []), { ...newAttendee, present: true }]);
    setNewAttendee({ name: "", email: "" });
  };
  const removeAttendee = (i) => setAttendees(m.attendees.filter((_, idx) => idx !== i));

  const setAgenda = (next) => patch({ agenda: next });
  const addAgenda = () => {
    if (!newAgenda) return;
    setAgenda([...(m.agenda || []), { title: newAgenda, notes: "" }]);
    setNewAgenda("");
  };
  const updateAgenda = (i, k, v) => {
    const next = [...m.agenda]; next[i] = { ...next[i], [k]: v }; setAgenda(next);
  };
  const removeAgenda = (i) => setAgenda(m.agenda.filter((_, idx) => idx !== i));

  const saveDiscussion = () => patch({ discussion });
  const saveMinutes = () => patch({ minutes });

  const addAction = async () => {
    if (!newAction.description || !newAction.owner_name) return toast.error("Description and owner required");
    try {
      const payload = {
        description: newAction.description,
        owner_name: newAction.owner_name,
        owner_email: newAction.owner_email || "",
        due_date: newAction.due_date || null,
        priority: newAction.priority || "normal",
      };
      await api.post(`/meetings/${id}/actions`, payload);
      await load();
      toast.success("Action added — task created");
      setNewAction({ description: "", owner_name: "", owner_email: "", due_date: "", priority: "normal" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : (detail || err?.message || "Failed to add action");
      console.error("addAction failed:", err);
      toast.error(msg);
    }
  };
  const removeAction = async (aid) => {
    try {
      await api.delete(`/meetings/${id}/actions/${aid}`);
      await load();
      toast.success("Action removed");
    } catch (err) {
      console.error("removeAction failed:", err);
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const onClosed = (data) => {
    const pdfId = data?.signed_pdf_file_id;
    if (pdfId) {
      setTimeout(() => downloadPdf(pdfId), 200);
    }
    load();
  };

  const downloadPdf = (fileId) => {
    const token = localStorage.getItem("opscmd_token");
    window.open(`${API}/files/${fileId}?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const presentCount = (m.attendees || []).filter((a) => a.present).length;
  const actions = m.corrective_actions || [];
  const agendaDiscussed = (m.agenda || []).filter((it) => it.discussed).length;

  const submitSignIn = async () => {
    if (!signedName.trim()) return toast.error("Enter your name to sign in");
    try {
      await api.post(`/meetings/${id}/sign-in`, { name: signedName.trim() });
      toast.success(`Signed in — logged for compliance`);
      setSignInOpen(false);
      setSignedName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleDiscussed = async (i, val) => {
    try {
      const r = await api.patch(`/meetings/${id}/agenda-discussed`, { index: i, discussed: val });
      setM({ ...m, agenda: r.data.agenda });
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Hero */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0">
            <button data-testid="back-button" onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-white/70 hover:text-white">
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
            <div className="mt-3 inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <Sparkles className="w-3 h-3 text-[#ff5f15]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{m.display_id ? `${m.display_id} · ` : ""}{m.status}</span>
            </div>
            <h1 className="mt-2 font-display font-black text-2xl lg:text-3xl tracking-tight leading-tight" data-testid="meeting-title">{m.title}</h1>
            <p className="mt-1 text-sm text-white/75">
              {presentCount} present · {(m.agenda || []).length} agenda items · {actions.length} corrective actions
              {savedAt && <span className="ml-2 text-white/55">· auto-saved {formatDateTime(savedAt)}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="signin-button" onClick={() => setSignInOpen(true)} className="btn-pill bg-white text-[#1a1a1a] hover:bg-[#ff4500] hover:text-white">
              <PenLine className="w-3.5 h-3.5" /> Sign in
            </button>
            {instance && (
              <button data-testid="open-report-pdf" onClick={() => downloadPdf(instance.pdf_file_id)} className="btn-pill btn-pill-ghost">
                <FileDown className="w-3.5 h-3.5" /> Report PDF
              </button>
            )}
            {m.signed_pdf_file_id && (
              <button data-testid="open-signed-pdf" onClick={() => downloadPdf(m.signed_pdf_file_id)} className="btn-pill btn-pill-primary">
                <FileDown className="w-3.5 h-3.5" /> Signed transcript
              </button>
            )}
            {m.status !== "completed" && (
              <button data-testid="complete-meeting-button" onClick={() => setCloseOpen(true)}
                className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] shadow-glow font-display font-black text-base px-5 py-2.5">
                <ShieldCheck className="w-4 h-4" /> Close out meeting & submit minutes
              </button>
            )}
          </div>
          {m.integrity_hash && (
            <div className="relative mt-3 inline-flex items-center gap-2 bg-white/8 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">Tamper-evident · SHA-256</span>
              <span className="font-mono text-[10px] text-white/90 truncate max-w-[260px]">{m.integrity_hash}</span>
            </div>
          )}
        </div>

        {/* Linked report KPIs */}
        {instance && (
          <div className="relative mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiInline label="Trend score" v={`${instance.data?.trend_score}/100`} />
            <KpiInline label="Runs" v={instance.data?.total_runs} />
            <KpiInline label="Failures" v={instance.data?.total_fails} />
            <KpiInline label="Fail rate" v={`${instance.data?.fail_rate_pct}%`} />
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Attendees */}
        <div className="surface-card rounded-2xl p-6">
          <SectionTitle icon={Users} title="Attendees" count={`${presentCount}/${(m.attendees || []).length}`} />
          <ul className="space-y-2">
            {(m.attendees || []).map((a, i) => (
              <li key={i} className="flex items-center gap-3" data-testid={`attendee-${i}`}>
                <button onClick={() => toggleAttendee(i)} className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-[11px] border-2 transition-colors duration-150 ${a.present ? "bg-[#1a8754] text-white border-[#1a8754]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/25"}`}>
                  {a.name?.[0]?.toUpperCase() || "?"}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{a.name}</div>
                  <div className="text-[10px] font-mono text-[#1a1a1a]/55 truncate">{a.email}</div>
                </div>
                <button onClick={() => removeAttendee(i)} className="text-[#ff4500] hover:bg-[#fff1ee] p-1.5 rounded-full">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
            {(m.attendees || []).length === 0 && <div className="text-[11px] font-mono text-[#1a1a1a]/55 uppercase tracking-wider">No attendees yet</div>}
          </ul>
          <div className="mt-4 space-y-2">
            <input data-testid="attendee-name" placeholder="Name" value={newAttendee.name} onChange={(e) => setNewAttendee({ ...newAttendee, name: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
            <input data-testid="attendee-email" placeholder="Email (optional)" value={newAttendee.email} onChange={(e) => setNewAttendee({ ...newAttendee, email: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
            <button data-testid="add-attendee" onClick={addAttendee} className="btn-pill btn-pill-primary w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add attendee</button>
          </div>
        </div>

        {/* Agenda + Discussion */}
        <div className="lg:col-span-2 space-y-5">
          <div className="surface-card rounded-2xl p-6">
            <SectionTitle icon={ClipboardList} title="Agenda & discussion" count={`${agendaDiscussed}/${(m.agenda || []).length}`} />
            <ul className="space-y-3">
              {(m.agenda || []).map((it, i) => (
                <li key={i} className={`border-l-2 pl-3 rounded-r-xl py-2 transition-colors duration-150 ${it.discussed ? "border-[#1a8754] bg-[#1a8754]/5" : "border-[#ff4500]"}`} data-testid={`agenda-${i}`}>
                  <div className="flex items-start gap-2">
                    <input data-testid={`agenda-discussed-${i}`} type="checkbox" checked={!!it.discussed}
                      onChange={(e) => toggleDiscussed(i, e.target.checked)}
                      className="mt-1.5 w-4 h-4 accent-[#1a8754]" />
                    <span className="font-mono text-[10px] text-[#ff4500] mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                    <input value={it.title} onChange={(e) => updateAgenda(i, "title", e.target.value)} onBlur={() => setAgenda(m.agenda)}
                      className="flex-1 bg-transparent border-0 font-display font-bold text-sm focus:outline-none" />
                    {it.source && it.source !== "system" && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#fff1ea] text-[#1a1a1a]">
                        {it.source.replace("_", " ")}
                      </span>
                    )}
                    <button onClick={() => removeAgenda(i)} className="text-[#ff4500] hover:bg-[#fff1ee] p-1 rounded-full">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <textarea value={it.notes || ""} onChange={(e) => updateAgenda(i, "notes", e.target.value)} onBlur={() => setAgenda(m.agenda)}
                    placeholder="Discussion notes…"
                    className="mt-1 w-full bg-white border border-[#1a1a1a]/10 rounded-xl px-3 py-2 text-sm h-16 focus:outline-none" />
                  {(it.photo_ids || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {it.photo_ids.map((pid) => (
                        <img key={pid} src={fileUrl(pid)} alt="" className="w-12 h-12 object-cover rounded-lg border border-[#1a1a1a]/10" title="Carried over from checklist" />
                      ))}
                    </div>
                  )}
                  {it.discussed && it.discussed_by && (
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[#1a8754]">
                      ✓ marked discussed by {it.discussed_by} · {formatDateTime(it.discussed_at)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex gap-2">
              <input data-testid="new-agenda-input" value={newAgenda} onChange={(e) => setNewAgenda(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAgenda(); } }}
                placeholder="Add agenda item…"
                className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
              <button data-testid="add-agenda-button" onClick={addAgenda} className="btn-pill btn-pill-secondary">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          </div>

          <div className="surface-card rounded-2xl p-6">
            <SectionTitle icon={MessageSquareText} title="General discussion" />
            <textarea data-testid="discussion-input" value={discussion} onChange={(e) => setDiscussion(e.target.value)} onBlur={saveDiscussion}
              placeholder="Open-floor discussion notes…"
              className="w-full bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-xl px-3 py-2 text-sm h-24 focus:outline-none" />
          </div>
        </div>
      </div>

      {/* Corrective actions */}
      <div className="surface-card rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <SectionTitle icon={ListChecks} title="Corrective action log" count={actions.length} />
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#ff4500] inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Each action creates a task automatically.
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 lg:grid-cols-5 gap-2">
          <input data-testid="action-description" value={newAction.description} onChange={(e) => setNewAction({ ...newAction, description: e.target.value })} placeholder="Action description"
            className="lg:col-span-2 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          <input data-testid="action-owner-name" value={newAction.owner_name} onChange={(e) => setNewAction({ ...newAction, owner_name: e.target.value })} placeholder="Owner name"
            className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          <input data-testid="action-due" type="date" value={newAction.due_date} onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
            className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <select data-testid="action-priority" value={newAction.priority} onChange={(e) => setNewAction({ ...newAction, priority: e.target.value })}
              className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <button data-testid="add-action-button" onClick={addAction} className="btn-pill btn-pill-primary">
              <Plus className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {actions.length === 0 && <div className="py-8 text-center bg-[#f7f5f2] rounded-xl border border-dashed border-[#1a1a1a]/15 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">No corrective actions yet</div>}
          {actions.map((a) => (
            <div key={a.id} className="bg-[#f7f5f2] rounded-xl p-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`action-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm">{a.description}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 mt-0.5">
                  Owner: <b className="text-[#1a1a1a]">{a.owner_name}</b>
                  {a.owner_email && ` · ${a.owner_email}`}
                  {a.due_date && ` · due ${formatDate(a.due_date)}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${a.priority === "critical" || a.priority === "high" ? "bg-[#ff4500] text-white" : "bg-[#1a1a1a]/10 text-[#1a1a1a]"}`}>
                  {a.priority}
                </span>
                {a.task_id && (
                  <Link to="/app/tasks" className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a] hover:text-[#ff4500] inline-flex items-center gap-1">
                    → Task
                  </Link>
                )}
                <button onClick={() => removeAction(a.id)} className="text-[#ff4500] hover:bg-white p-1.5 rounded-full">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Minutes */}
      <div className="surface-card rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <SectionTitle icon={Save} title="Meeting minutes" />
          <button data-testid="save-minutes-button" onClick={saveMinutes} className="btn-pill btn-pill-secondary">Save</button>
        </div>
        <textarea data-testid="minutes-input" value={minutes} onChange={(e) => setMinutes(e.target.value)} onBlur={saveMinutes}
          placeholder="Long-form meeting minutes for the record…"
          className="mt-2 w-full bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-xl px-3 py-2 text-sm h-40 focus:outline-none" />
      </div>

      {/* Sign-in dialog */}
      <Dialog open={signInOpen} onOpenChange={(o) => !o && setSignInOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl border-[#1a1a1a]/15">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#ff4500]" /> Sign in for compliance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#1a1a1a]/70">Type your name to log attendance. Your name, account email, and current timestamp are recorded for the compliance trail.</p>
            <input data-testid="signin-name-input" value={signedName} onChange={(e) => setSignedName(e.target.value)}
              placeholder="Type your full name…"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-3 text-base font-display font-bold focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">Will be recorded as signed_in_at · {formatDateTime(new Date())}</div>
          </div>
          <DialogFooter>
            <button data-testid="signin-submit" onClick={submitSignIn} className="btn-pill btn-pill-primary">
              <PenLine className="w-3.5 h-3.5" /> Sign in
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CloseOutDialog open={closeOpen} onClose={() => setCloseOpen(false)}
        kind="meeting" title={m.title}
        endpoint={`/meetings/${id}/complete`}
        onClosed={onClosed} />

      {/* Big bottom-of-page close-out CTA */}
      {m.status !== "completed" && (
        <div className="surface-card rounded-2xl p-5 mt-2 border-2 border-[#ff4500]/30 bg-gradient-to-br from-[#fff1ea] to-white" data-testid="closeout-cta-meeting">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Final step</div>
              <div className="font-display font-black text-xl">Ready to lock the record?</div>
              <p className="text-[12px] text-[#1a1a1a]/70 mt-1 max-w-xl">
                Closing out generates a tamper-evident signed PDF (SHA-256 hash) of attendance, agenda, discussion, minutes, and corrective actions — then optionally emails / posts it to your configured destinations.
              </p>
            </div>
            <button data-testid="closeout-bottom-meeting" onClick={() => setCloseOpen(true)}
              className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] shadow-glow font-display font-black text-base px-6 py-3 self-start md:self-auto whitespace-nowrap">
              <ShieldCheck className="w-4 h-4" /> Close out meeting & submit minutes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, count }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-[#fff1ea] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#1a1a1a]" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Section</div>
          <div className="font-display font-bold text-base">{title}</div>
        </div>
      </div>
      {count !== undefined && (
        <span className="bg-[#1a1a1a]/8 text-[#1a1a1a] text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">{count}</span>
      )}
    </div>
  );
}

function KpiInline({ label, v }) {
  return (
    <div className="bg-white/10 rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/65">{label}</div>
      <div className="font-display font-black text-xl">{v}</div>
    </div>
  );
}
